import React from 'react';
import { QuickAction } from '../ResultQuickActionMenu';
import {
  SearchContentProvider,
  SearchProviderContext,
  SearchResultItem,
} from './types';
import {
  EmailIcon,
  DeleteIcon,
} from './icons';

// Recent email storage constants
const RECENT_EMAILS_KEY = 'flik.recentEmails';
const MAX_RECENT_EMAILS = 200;

// Recent email storage utility functions
const getRecentEmails = (): string[] => {
  try {
    const data = localStorage.getItem(RECENT_EMAILS_KEY);
    if (!data) return [];
    return JSON.parse(data);
  } catch {
    return [];
  }
};

export class EmailProvider implements SearchContentProvider {
  id = 'email-provider';
  name = 'Email Provider';
  priority = 25; // Lower priority than system items

  getResults(context: SearchProviderContext): SearchResultItem[] {
    const { searchQuery, badges, removeRecentEmail } = context;

    // Get recent emails from storage
    const recentEmails = getRecentEmails();

    // Filter emails based on search query and badges
    const filteredRecentEmails = recentEmails
      .filter((email) => {
        // Don't show emails that are already added as badges
        if (
          badges.some((badge) => badge.toLowerCase() === email.toLowerCase())
        ) {
          return false;
        }

        // Show emails if there's a search query OR badges
        const hasSearchQuery = searchQuery.trim().length > 0;
        const hasBadges = badges.length > 0;

        if (!hasSearchQuery && !hasBadges) {
          return false;
        }

        // Special case: if query starts with "@", show all emails
        if (hasSearchQuery && searchQuery.trim().startsWith('@')) {
          return true;
        }

        // Check if email matches search query
        const matchesSearchQuery =
          !hasSearchQuery ||
          email.toLowerCase().includes(searchQuery.toLowerCase());

        // Check if email matches any badge (OR operation for badges)
        const matchesBadges =
          !hasBadges ||
          badges.some((badge) =>
            email.toLowerCase().includes(badge.toLowerCase()),
          );

        // Email should match either search query OR badges
        return matchesSearchQuery || matchesBadges;
      })
      .slice(0, 10); // Limit to 10 recent emails in results

    // Create email items that can be added as badges
    return filteredRecentEmails.map((email, index) => {
      // Create quick actions for the kebab menu
      const quickActions: QuickAction[] = [];

      if (removeRecentEmail) {
        quickActions.push({
          id: 'delete-email',
          label: 'Delete',
          tooltip: 'Remove recent email',
          icon: <DeleteIcon />,
          onClick: () => removeRecentEmail(email),
          destructive: true,
        });
      }

      return {
        id: `email-${1000 + index}`, // Use high ID to avoid conflicts
        title: email,
        subtitle: 'Recent email address',
        type: 'email',
        priority: 25,
        icon: <EmailIcon />,
        canAddAsBadge: true,
        badgeValue: email, // The value to add as a badge
        quickActions: quickActions.length > 0 ? quickActions : undefined,
      };
    });
  }

  isActive(context: SearchProviderContext): boolean {
    return context.isLoggedIn;
  }
}
